<script lang="ts">
	// Island: web push opt-in card. Shown from the 4th page view on, re-asked after 15 days
	// when dismissed; an already granted permission is re-subscribed silently.
	import Config from "./index";
	import { onMount } from "svelte";
	import { fade } from "svelte/transition";
	import {
		subscribe,
		getPermissionStatus,
	} from "@reperio-news/core.components/PushSubscription.svelte";

	const PAGE_VIEWS_BEFORE_ASK = 3;
	const RE_ASK_AFTER_MS = 86400000 * 15;

	//	plain const, not a TS enum (esbuild inlines enum reads and drops the declaration
	//	the template still references at runtime)
	const Result = { None: 0, Pending: 1, Done: 2, Failed: 3 } as const;

	let show = false;
	let result: number = Result.None;

	onMount(async () => {
		//	never on the internal alias domain; localhost is allowed so the verify suite can drive the card
		const host = window.location.hostname;
		if (
			window.location.href.indexOf("_reperio_admin_preview") !== -1 ||
			(host !== Config.domain && host !== "localhost" && host !== "127.0.0.1") ||
			!("Notification" in window)
		) {
			return;
		}

		let views = parseInt(localStorage.getItem("reperio_push_sub_i") || "0", 10);
		if (isNaN(views) || views > 5) views = 0;

		if (views < PAGE_VIEWS_BEFORE_ASK) {
			localStorage.setItem("reperio_push_sub_i", (views + 1).toString());
			return;
		}

		const status = await getPermissionStatus();

		if (status === "granted") {
			subscribe();
			return;
		}

		const lastAsk = localStorage.getItem("reperio_push_sub_last_ask");
		show =
			status !== "denied" &&
			(!lastAsk || Date.now() - new Date(lastAsk).getTime() > RE_ASK_AFTER_MS);
	});

	function dismiss() {
		localStorage.setItem("reperio_push_sub_last_ask", new Date().toISOString());
		show = false;
	}

	async function accept() {
		result = Result.Pending;
		//	the browser prompt may sit unanswered: close the card, ask again later
		const t = setTimeout(dismiss, 8000);
		try {
			const ok = await subscribe();
			clearTimeout(t);
			result = ok ? Result.Done : Result.Failed;
			localStorage.setItem("reperio_push_sub_last_ask", new Date().toISOString());
			setTimeout(() => (show = false), ok ? 2500 : 5000);
		} catch (e) {
			clearTimeout(t);
			console.error(e);
			dismiss();
		}
	}
</script>

{#if show}
	<aside class="af-push" role="region" aria-label="Notificări" transition:fade={{ duration: 200 }}>
		<img class="af-push__icon" src="/favicon-96.png" width="40" height="40" alt="" />
		<div class="af-push__body">
			<p class="af-push__kicker af-mono">Notificări</p>
			{#if result === Result.None}
				<p class="af-push__text">Primește pe loc cele mai importante știri economice și financiare.</p>
				<div class="af-push__actions">
					<button type="button" class="af-push__btn af-push__btn--primary" on:click={accept}>Activează</button>
					<button type="button" class="af-push__btn" on:click={dismiss}>Nu, mulțumesc</button>
				</div>
			{:else if result === Result.Pending}
				<p class="af-push__text">Confirmă în fereastra browserului.</p>
			{:else if result === Result.Done}
				<p class="af-push__text">Gata. Îți trimitem cele mai importante știri ale zilei.</p>
			{:else}
				<p class="af-push__text">Notificările sunt blocate în browser. Le poți activa oricând din setările site-ului.</p>
			{/if}
		</div>
		{#if result === Result.None}
			<button type="button" class="af-push__close" aria-label="Închide" on:click={dismiss}>
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
			</button>
		{/if}
	</aside>
{/if}

<style lang="scss">
	.af-push {
		position: fixed;
		z-index: 60;
		left: var(--af-gutter);
		right: var(--af-gutter);
		bottom: var(--af-gutter);
		display: flex;
		gap: var(--af-space-3);
		padding: var(--af-space-4);
		background: var(--af-surface);
		color: var(--af-ink);
		border: 1px solid var(--af-hairline);
		border-radius: var(--af-radius-card);
		box-shadow: var(--af-shadow-card-hover);
		font: 400 var(--af-text-ui) / var(--af-leading-ui) var(--af-font-sans);
	}

	@media (min-width: 768px) {
		.af-push {
			left: auto;
			right: var(--af-space-6);
			bottom: var(--af-space-6);
			width: 380px;
		}
	}

	.af-push__icon {
		flex: 0 0 auto;
		width: 40px;
		height: 40px;
		border-radius: 8px;
	}

	.af-push__body {
		flex: 1 1 auto;
		min-width: 0;
	}

	.af-push__kicker {
		margin: 0 0 var(--af-space-1);
		font-size: var(--af-text-kicker);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--af-ink-muted);
	}

	.af-push__text {
		margin: 0;
	}

	.af-push__actions {
		display: flex;
		flex-wrap: wrap;
		gap: var(--af-space-2);
		margin-top: var(--af-space-3);
	}

	.af-push__btn {
		display: inline-flex;
		align-items: center;
		min-height: 36px;
		padding: 0 var(--af-space-4);
		border: 0;
		border-radius: var(--af-radius-pill);
		font: 500 var(--af-text-ui) / 1 var(--af-font-sans);
		background: var(--af-surface-sunk);
		color: var(--af-ink);
		cursor: pointer;
		transition:
			background-color 0.15s var(--af-ease-out),
			color 0.15s var(--af-ease-out);

		&:hover {
			background: var(--af-hairline);
		}

		&:focus-visible {
			outline: 2px solid var(--af-focus);
			outline-offset: 2px;
		}
	}

	.af-push__btn--primary {
		background: var(--af-brand);
		color: var(--af-ink-inverse);

		&:hover {
			background: var(--af-brand-hover);
		}
	}

	.af-push__close {
		position: absolute;
		top: var(--af-space-2);
		right: var(--af-space-2);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		padding: 0;
		border: 0;
		border-radius: var(--af-radius-pill);
		background: transparent;
		color: var(--af-ink-muted);
		cursor: pointer;

		&:hover {
			color: var(--af-ink);
			background: var(--af-surface-sunk);
		}
	}

	@media (max-width: 767px) {
		.af-push__btn {
			min-height: 44px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.af-push__btn {
			transition: none;
		}
	}
</style>
